import { gaCurrency, trackEvent } from '../hooks/useGoogleAnalytics'
import { collectFunnelStep } from './collector'

/**
 * GA4 recommended e-commerce events, in the community's own Google Analytics.
 *
 * Names and parameters follow Google's recommended events
 * (view_item_list → select_item → view_item → begin_checkout →
 * add_payment_info → purchase, and refund). Amounts are in currency units
 * (99.9), never cents. No personal data is ever a parameter.
 *
 * `purchase` is sent once per order: the transaction id is the purchase id
 * (the same one the server-side Measurement Protocol uses), and the ids already
 * sent are remembered in localStorage so a reload, a second tab or the
 * confirmation page cannot count the order again.
 */

export type GaItemCategory = 'product' | 'plan' | 'add_on'

export interface GaItem {
  item_id: string
  item_name: string
  item_category: GaItemCategory
  item_variant?: string
  price?: number
  quantity: number
  index?: number
  item_list_id?: string
  item_list_name?: string
}

/** What a product looks like to analytics, whatever endpoint it came from. */
interface ProductLike {
  id: string
  title?: string | null
  name?: string | null
  price?: string | number | null
  type?: string | null
  billingInterval?: string | null
}

const amount = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : undefined
}

/** A subscription option is a plan; anything else sold on its own is a product. */
export const categoryOf = (product: Pick<ProductLike, 'type' | 'billingInterval'>): GaItemCategory =>
  product.type === 'SUBSCRIPTION' || product.billingInterval ? 'plan' : 'product'

export function gaItem(
  product: ProductLike,
  options: { category?: GaItemCategory; name?: string | null; variant?: string | null; price?: string | number | null; index?: number } = {}
): GaItem {
  const price = amount(options.price !== undefined ? options.price : product.price)
  return {
    item_id: product.id,
    item_name: options.name || product.title || product.name || product.id,
    item_category: options.category || categoryOf(product),
    ...(options.variant ? { item_variant: options.variant } : {}),
    ...(price !== undefined ? { price } : {}),
    quantity: 1,
    ...(options.index !== undefined ? { index: options.index } : {})
  }
}

const sumOf = (items: GaItem[]) => Math.round(items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0) * 100) / 100

/** PIX, boleto or card, as GA's `payment_type`. */
export const paymentTypeOf = (method: string | null | undefined): string | undefined => {
  switch (method) {
    case 'PIX':
      return 'pix'
    case 'BOLETO':
      return 'boleto'
    case 'CREDIT_CARD':
    case 'CARD':
      return 'credit_card'
    case 'FREE':
      return 'free'
    default:
      return undefined
  }
}

export function trackViewItemList(listId: string, listName: string, items: GaItem[]) {
  if (items.length === 0) return
  /* The same moments feed the community's own funnel (F-88): catalog, product, checkout data, payment. */
  collectFunnelStep('catalog')
  trackEvent('view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: items.map((item, index) => ({ index, ...item, item_list_id: listId, item_list_name: listName }))
  })
}

export function trackSelectItem(listId: string, listName: string, item: GaItem) {
  trackEvent('select_item', {
    item_list_id: listId,
    item_list_name: listName,
    items: [{ ...item, item_list_id: listId, item_list_name: listName }]
  })
}

export function trackViewItem(item: GaItem, currency?: string | null) {
  collectFunnelStep('product', item.item_id)
  trackEvent('view_item', { currency: currency || gaCurrency(), value: item.price ?? 0, items: [item] })
}

export interface GaCart {
  items: GaItem[]
  /** The total the buyer sees; defaults to the items' sum. */
  value?: number | null
  currency?: string | null
  coupon?: string | null
}

const cartParams = (cart: GaCart) => ({
  currency: cart.currency || gaCurrency(),
  value: amount(cart.value ?? undefined) ?? sumOf(cart.items),
  ...(cart.coupon ? { coupon: cart.coupon } : {}),
  items: cart.items
})

export function trackBeginCheckout(cart: GaCart) {
  if (cart.items.length === 0) return
  collectFunnelStep('checkout_identify', cart.items[0].item_id)
  trackEvent('begin_checkout', cartParams(cart))
}

export function trackAddPaymentInfo(cart: GaCart, method: string | null | undefined) {
  if (cart.items.length === 0) return
  collectFunnelStep('checkout_payment', cart.items[0].item_id)
  const paymentType = paymentTypeOf(method)
  trackEvent('add_payment_info', { ...cartParams(cart), ...(paymentType ? { payment_type: paymentType } : {}) })
}

/* ---------- purchase: once per order ---------- */

const SENT_KEY = 'memberfy:ga:purchases'
const ORDER_KEY = (purchaseId: string) => `memberfy:ga:order:${purchaseId}`
const MAX_SENT = 200
const ORDER_TTL_MS = 7 * 24 * 60 * 60 * 1000
/* Also in memory: with storage blocked, this tab still never sends twice. */
const sentInMemory = new Set<string>()

const readSent = (): string[] => {
  try {
    const list = JSON.parse(localStorage.getItem(SENT_KEY) || '[]')
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

const wasSent = (transactionId: string) => sentInMemory.has(transactionId) || readSent().includes(transactionId)

const markSent = (transactionId: string) => {
  sentInMemory.add(transactionId)
  try {
    const list = readSent().filter((id) => id !== transactionId)
    list.push(transactionId)
    localStorage.setItem(SENT_KEY, JSON.stringify(list.slice(-MAX_SENT)))
  } catch {
    /* Private mode or full storage: the in-memory set still guards this tab. */
  }
}

/** An order waiting for its payment (PIX, boleto, card in review), as it will be reported once paid. */
export interface GaOrder extends GaCart {
  paymentType?: string
  savedAt: number
}

/**
 * Keeps what the order was, by purchase id, so whichever screen sees the
 * payment confirmed (the dialog that created it, or the payment page after a
 * reload) can report the same `purchase`.
 */
export function rememberOrder(purchaseId: string, cart: GaCart, method?: string | null) {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i)
      if (!key?.startsWith('memberfy:ga:order:')) continue
      const old = JSON.parse(localStorage.getItem(key) || 'null') as GaOrder | null
      if (!old || Date.now() - old.savedAt > ORDER_TTL_MS) localStorage.removeItem(key)
    }
    const order: GaOrder = { ...cart, paymentType: paymentTypeOf(method), savedAt: Date.now() }
    localStorage.setItem(ORDER_KEY(purchaseId), JSON.stringify(order))
  } catch {
    /* Without storage, only the screen that created the order can report it. */
  }
}

export function readOrder(purchaseId: string): GaOrder | null {
  try {
    return JSON.parse(localStorage.getItem(ORDER_KEY(purchaseId)) || 'null')
  } catch {
    return null
  }
}

/**
 * The order is paid (or claimed for free): `purchase`, once per transaction id.
 * Without a cart, the one remembered for the order is used.
 */
export function trackPurchase(transactionId: string | null | undefined, cart?: GaCart | null, method?: string | null) {
  if (!transactionId || wasSent(transactionId)) return
  const order = cart || readOrder(transactionId)
  if (!order || order.items.length === 0) return
  const paymentType = paymentTypeOf(method) || (order as GaOrder).paymentType
  trackEvent('purchase', {
    transaction_id: transactionId,
    ...cartParams(order),
    ...(paymentType ? { payment_type: paymentType } : {})
  })
  markSent(transactionId)
  try {
    localStorage.removeItem(ORDER_KEY(transactionId))
  } catch {
    /* Nothing to clean up. */
  }
}

/** A refund made by the community's team, on the original transaction. */
export function trackRefund(transactionId: string, value: number | string | null | undefined, currency?: string | null) {
  trackEvent('refund', {
    transaction_id: transactionId,
    currency: currency || gaCurrency(),
    ...(amount(value) !== undefined ? { value: amount(value) } : {})
  })
}
